import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Seo } from "@/components/Seo";
import { ShieldCheck, Layers, Gauge, ListChecks, ChevronDown, AlertTriangle } from "lucide-react";

const MAT_COLOR = ["#dc2626", "#dc2626", "#ea580c", "#d97706", "#65a30d", "#059669"];
const REGION_CHIP = {
  Global: "bg-slate-100 text-slate-600",
  EU: "bg-blue-50 text-blue-700",
  "US / Global": "bg-indigo-50 text-indigo-700",
};

const ringStyle = (pct) => {
  const C = 2 * Math.PI * 26;
  return { dash: `${(C * pct) / 100} ${C}`, C };
};

function CoverageRing({ pct, color }) {
  const { dash } = ringStyle(pct);
  return (
    <svg viewBox="0 0 64 64" className="h-16 w-16">
      <circle cx="32" cy="32" r="26" fill="none" stroke="#e5e7eb" strokeWidth="7" />
      <circle
        cx="32" cy="32" r="26" fill="none" stroke={color} strokeWidth="7"
        strokeDasharray={dash} strokeLinecap="round"
        transform="rotate(-90 32 32)"
        style={{ transition: "stroke-dasharray 0.9s ease" }}
      />
      <text x="32" y="33" textAnchor="middle" dominantBaseline="middle" className="fill-ek-ink" style={{ fontSize: 14, fontWeight: 800 }}>
        {pct}%
      </text>
    </svg>
  );
}

function MaturityDots({ value }) {
  return (
    <div className="flex items-center gap-1" title={`Maturity ${value}/5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span
          key={i}
          className="h-2 w-2 rounded-full"
          style={{ backgroundColor: i <= Math.round(value) ? MAT_COLOR[Math.round(value)] : "#e5e7eb" }}
        />
      ))}
    </div>
  );
}

function FrameworkCard({ f }) {
  const [open, setOpen] = useState(false);
  const color = f.coverage >= 80 ? "#059669" : f.coverage >= 60 ? "#d97706" : "#dc2626";
  return (
    <div className="rounded-md border border-black/5 bg-white p-5" data-testid={`framework-card-${f.id}`}>
      <div className="flex items-start gap-4">
        <CoverageRing pct={f.coverage} color={color} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-base font-semibold text-ek-ink">{f.short}</h3>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${REGION_CHIP[f.region] || "bg-slate-100 text-slate-600"}`}>
              {f.region}
            </span>
          </div>
          <p className="mt-0.5 truncate text-xs text-ek-ink/65">{f.name}</p>
          <div className="mt-2 flex items-center gap-3">
            <MaturityDots value={f.maturity} />
            <span className="text-xs text-ek-ink/60">
              {f.maturity_tier} · {f.covered_controls}/{f.total_controls} controls
            </span>
          </div>
        </div>
      </div>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`framework-toggle-${f.id}`}
        className="mt-3 flex items-center gap-1 text-xs font-semibold text-ek-purple hover:underline"
      >
        Coverage & gaps <ChevronDown size={13} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-3 space-y-2 border-t border-black/5 pt-3">
          {f.groups.map((g) => (
            <div key={g.group_id}>
              <div className="flex items-center gap-2">
                <span className="w-44 shrink-0 truncate text-xs text-ek-ink/70">{g.group_name}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-black/[0.06]">
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${g.coverage}%`, backgroundColor: g.coverage >= 80 ? "#059669" : g.coverage >= 50 ? "#d97706" : "#dc2626" }}
                  />
                </div>
                <span className="w-14 shrink-0 text-right text-[11px] tabular-nums text-ek-ink/65">{g.covered}/{g.total}</span>
              </div>
              {g.gaps.length > 0 && (
                <div className="ml-44 mt-1 flex flex-wrap gap-1 pl-2">
                  {g.gaps.map((gap) => (
                    <span key={gap.ref} className="inline-flex items-center gap-1 rounded bg-red-50 px-1.5 py-0.5 text-[10px] text-red-700" title={gap.title}>
                      <AlertTriangle size={9} /> {gap.ref}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const FW_LABEL = { iso31000: "ISO 31000", coso_erm: "COSO ERM", nist_csf_2: "NIST CSF", iso27001_2022: "ISO 27001", dora: "DORA" };

export default function Compliance() {
  const [data, setData] = useState(null);
  const [controls, setControls] = useState([]);

  useEffect(() => {
    api.get("/compliance/summary").then(({ data }) => setData(data)).catch(() => {});
    api.get("/controls").then(({ data }) => setControls(data.items)).catch(() => {});
  }, []);

  const o = data?.overall;

  return (
    <div className="min-h-screen bg-ek-canvas">
      <Seo title="Compliance & Standards" description="Benchmark risk posture against ISO 31000, COSO ERM, NIST CSF 2.0, ISO 27001 and DORA." path="/compliance" />
      <Navbar />
      <main className="mx-auto max-w-7xl px-6 py-10" data-testid="compliance-page">
        <h1 className="ek-eyebrow">Standards spine</h1>
        <h2 className="mt-1 font-display text-3xl font-bold text-ek-ink">Compliance &amp; benchmarking</h2>
        <p className="mt-2 max-w-2xl text-sm text-ek-ink/65">
          Every internal control is crosswalked to the globally recognised frameworks, giving live coverage and maturity you can benchmark and present to regulators.
        </p>

        {/* Overall posture strip */}
        {o && (
          <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4" data-testid="compliance-overall">
            <Kpi icon={ShieldCheck} label="Overall coverage" value={`${o.coverage}%`} />
            <Kpi icon={Gauge} label="Maturity" value={`${o.maturity} · ${o.maturity_tier}`} />
            <Kpi icon={ListChecks} label="Controls" value={o.control_count} />
            <Kpi icon={Layers} label="Frameworks" value={o.framework_count} />
          </div>
        )}

        {/* Framework benchmark grid */}
        <h3 className="mt-10 ek-eyebrow">Framework benchmark</h3>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data?.frameworks.map((f) => (
            <FrameworkCard key={f.id} f={f} />
          ))}
        </div>

        {/* Control crosswalk */}
        <h3 className="mt-10 ek-eyebrow">Control crosswalk</h3>
        <div className="mt-4 overflow-x-auto rounded-md border border-black/5 bg-white" data-testid="control-crosswalk">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-ek-ink/65">
                <th className="px-4 py-3 font-medium">Control</th>
                <th className="px-4 py-3 font-medium">Maturity</th>
                <th className="px-4 py-3 font-medium">Mapped to</th>
              </tr>
            </thead>
            <tbody>
              {controls.map((c) => (
                <tr key={c.control_id} className="border-b border-black/[0.04] last:border-0" data-testid={`control-row-${c.control_id}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-ek-ink">{c.title}</div>
                    <div className="text-xs text-ek-ink/65">{c.control_id}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-2">
                      <MaturityDots value={c.maturity} />
                      <span className="text-xs text-ek-ink/65">{c.maturity_tier}</span>
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {Object.keys(c.refs).map((fid) => (
                        <span key={fid} className="rounded-full border border-ek-purple/15 bg-ek-purple/5 px-2 py-0.5 text-[10px] font-semibold text-ek-purple" title={c.refs[fid].join(", ")}>
                          {FW_LABEL[fid] || fid}
                        </span>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}

const Kpi = ({ icon: Icon, label, value }) => (
  <div className="rounded-md border border-black/5 bg-white p-4">
    <div className="flex items-center gap-2 text-ek-ink/65">
      <Icon size={14} />
      <span className="text-xs font-medium uppercase tracking-wide">{label}</span>
    </div>
    <p className="mt-2 font-display text-2xl font-bold text-ek-ink">{value}</p>
  </div>
);
